import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChatCircleDots, Smiley, SmileyMeh, SmileySad, PaperPlaneRight } from "@phosphor-icons/react";

const SENTIMENTS = [
  { key: "love", label: "Love it", icon: Smiley, color: "var(--bk-success)" },
  { key: "ok", label: "It's okay", icon: SmileyMeh, color: "#8A6A3B" },
  { key: "frustrated", label: "Frustrated", icon: SmileySad, color: "#B23B1F" },
];

export function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sentiment, setSentiment] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post("/feedback", { message, sentiment });
      toast.success("Thank you — your feedback was sent!");
      setOpen(false);
      setMessage("");
      setSentiment("");
    } catch {
      toast.error("Couldn't send feedback. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="open-feedback"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold border shadow-md transition-transform hover:-translate-y-0.5"
        style={{ background: "#fff", borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
      >
        <ChatCircleDots size={20} weight="duotone" color="var(--bk-accent)" /> Feedback
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send us feedback</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="feedback-form">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-semibold mb-2" style={{ color: "var(--bk-text-2)" }}>How's it going?</p>
              <div className="flex gap-2">
                {SENTIMENTS.map((s) => {
                  const active = sentiment === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSentiment(s.key)}
                      data-testid={`feedback-sentiment-${s.key}`}
                      className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-md border transition-colors"
                      style={{ borderColor: active ? s.color : "var(--bk-border)", background: active ? "var(--bk-page)" : "#fff" }}
                    >
                      <s.icon size={26} weight={active ? "fill" : "duotone"} color={s.color} />
                      <span className="text-xs font-medium" style={{ color: "var(--bk-text-2)" }}>{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Your thoughts</label>
              <textarea
                data-testid="feedback-message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="What's working? What's confusing? What would make this a must-have for you?"
                className="w-full mt-1.5 px-4 py-3 rounded-md border bg-white text-sm outline-none focus:ring-2 leading-relaxed"
                style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
              />
            </div>
            <button
              type="submit"
              disabled={sending || !message.trim()}
              data-testid="feedback-submit"
              className="bk-btn-primary w-full py-2.5 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send feedback"} <PaperPlaneRight size={16} weight="bold" />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
